// @todo: Migrate to 'Dialog.Popover' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import * as Popover from '@radix-ui/react-popover';
// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {useIsAdminModeActiveAndIsNotLocalEnv, viewingAs} from '../util/admin';
import {usePublicQueueAccess, useUserEnhancedQueueItems} from './Launch/hooks';
import {
  WANDB_ENTITY_NAME,
  WANDB_PUBLIC_QUEUE_NAME,
} from './Launch/publicQueue/constants';
import {usePublicQueuesLite, useQueueLite} from './Launch/Queues/hooks';
import {type Queue} from './Launch/Queues/queueUtil';
import {RunQueueItemsPopoverContent} from './Launch/RunQueueItemsPopoverContent';
import * as S from './RunQueueItemsPopover.styles';

interface RunQueuePopoverContextType {
  openPopover: () => void;
  registerPopover: (openFn: () => void) => void;
  unregisterPopover: () => void;
}

const RunQueuePopoverContext = createContext<RunQueuePopoverContextType | null>(
  null
);

export const useRunQueuePopover = () => {
  const context = useContext(RunQueuePopoverContext);
  if (!context) {
    throw new Error(
      'useRunQueuePopover must be used within RunQueuePopoverProvider'
    );
  }
  return context;
};

export const RunQueuePopoverProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const popoverOpenFnRef = useRef<(() => void) | null>(null);

  const openPopover = useCallback(() => {
    if (popoverOpenFnRef.current) {
      popoverOpenFnRef.current();
    }
  }, []);

  const registerPopover = useCallback((openFn: () => void) => {
    popoverOpenFnRef.current = openFn;
  }, []);

  const unregisterPopover = useCallback(() => {
    popoverOpenFnRef.current = null;
  }, []);

  const value = useMemo(
    () => ({openPopover, registerPopover, unregisterPopover}),
    [openPopover, registerPopover, unregisterPopover]
  );

  return (
    <RunQueuePopoverContext.Provider value={value}>
      {children}
    </RunQueuePopoverContext.Provider>
  );
};

export const RunQueueItemPopover: React.FC = () => {
  const isAdminMode =
    useIsAdminModeActiveAndIsNotLocalEnv() || viewingAs() !== '';
  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();
  const [open, setOpen] = useState(false);
  const context = useContext(RunQueuePopoverContext);

  const {queues, error: publicQueuesError} = usePublicQueuesLite(!open);

  const publicQueue = useMemo(
    () =>
      queues.find(
        q =>
          q.name === WANDB_PUBLIC_QUEUE_NAME &&
          q.entityName === WANDB_ENTITY_NAME
      ),
    [queues]
  );

  const emptyArrayRef = useRef<Queue[]>([]);
  const stablePublicQueue = useMemo(
    () => publicQueue ?? emptyArrayRef.current,
    [publicQueue]
  );

  const {queue, refetch, error} = useQueueLite(
    WANDB_ENTITY_NAME,
    publicQueue?.id
  );

  const openFn = useCallback(() => {
    refetch?.();
    setOpen(true);
  }, [refetch]);

  // Register this popover with the global context
  useEffect(() => {
    if (context) {
      context.registerPopover(openFn);
      return () => {
        context.unregisterPopover();
      };
    }
    return undefined;
  }, [context, openFn]);

  const {items: userRunQueueItems, loading: enhancedItemsLoading} =
    useUserEnhancedQueueItems(stablePublicQueue);

  if (isFeatureFlagLoading || !hasPublicQueueAccess) {
    return <></>;
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen} modal={true}>
      <S.IconContainer>
        <Tooltip
          position="bottom center"
          trigger={
            <Popover.Trigger asChild>
              <S.IconWrapper
                $showAdminBanner={isAdminMode}
                $isOpen={open}
                // prevent surrounding text selection when clicking the icon
                onMouseDown={e => e.preventDefault()}>
                <S.Icon aria-label="run queue items" />
              </S.IconWrapper>
            </Popover.Trigger>
          }>
          View recent runs
        </Tooltip>
      </S.IconContainer>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          alignOffset={16}
          style={{outline: 'none', zIndex: 9999}}>
          <RunQueueItemsPopoverContent
            queue={queue}
            items={userRunQueueItems}
            loading={enhancedItemsLoading}
            error={error || publicQueuesError}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
